/* eslint-disable react-hooks/exhaustive-deps */
import React, { FC, useCallback, useEffect, useState } from 'react';
import { Accordion, AccordionDetails, AccordionSummary, Button, Grid, Typography } from '@material-ui/core';
import { ExpandLess } from '@material-ui/icons';
import { useSnackbar } from 'notistack';
import { CashbackItemIcon, DeleteIcon, EditIcon, Header, PageTitle, RoundBtnIcon, AddIcon } from '../../components';
import { useStyles } from './styles';
import { useApiError, useAppData, useLoading } from '../../providers';
import { AddEditCampaignDialog, AddEditRuleDialog, CampaignEnableSwitch, DeleteConfirmationDialog, EditDefaultCampaignDialog, QuickReport, RulesTable, PaymentsTable } from './components';
import { addCampaign, addRule, deleteCampaign, deleteRule, editCampaign, editRule, getAllCampaigns, getCampaign, getCashbackPayments, getRule, getRuleSetup } from './CashbackService';
import { CalculationPeriod, Campaign, CampaignRule, CampaignRuleSetup, CampaignView, EditCampaign, EditCampaignRule, Payments } from './CashbackModel';
import { useCommonStyles } from '../../styles';
import { concatErrorMessages, formatDate } from '../../utils';

export const Cashback: FC = () => {
  const rowCount = 40;
  const classes = useStyles();
  const commonClasses = useCommonStyles();
  const { translations, userData, locale } = useAppData();
  const { enqueueSnackbar } = useSnackbar();
  const { addError } = useApiError();
  const [translation, setTranslation] = useState<any>([]);
  const { setIsLoading } = useLoading();
  const [partnerId, setPartnerId] = useState<number>();
  const [campaignDialogTrigger, setCampaignDialogTrigger] = useState<boolean>(false);
  const [defaultCampaignDialogTrigger, setDefaultCampaignDialogTrigger] = useState<boolean>(false);
  const [ruleDialogTrigger, setRuleDialogTrigger] = useState<boolean>(false);
  const [campaigns, setCampaigns] = useState<CampaignView[]>([]);
  const [expanded, setExpanded] = useState<number | false>(false);
  const [rowsNumber, setRowsNumber] = useState<number>(rowCount);
  const [selectedCampaign, setSelectedCampaign] = useState<Campaign>();
  const [selectedRule, setSelectedRule] = useState<CampaignRule>();
  const [ruleSetup, setRuleSetup] = useState<CampaignRuleSetup>();
  const [selectedCampaignId, setSelectedCampaignId] = useState<number>();
  const [deleteConfirmDialog, setDeleteConfirmDialog] = useState<boolean>(false);
  const [payments, setPayments] = useState<Payments[]>([]);

  const handleShowMore = useCallback(() => {
    const newNumber = rowsNumber + rowCount;
    setRowsNumber(newNumber);
  }, [rowsNumber, rowCount]);

  const getPayments = useCallback(async () => {
    try {
      setIsLoading(true);
      const response = await getCashbackPayments();
      setPayments(response.data);
    } catch (error) {
      addError(JSON.stringify(error.response));
    } finally {
      setIsLoading(false);
    }
  }, [setIsLoading, addError]);

  const getCampaigns = useCallback(
    async (partnerID) => {
      try {
        setIsLoading(true);
        const response = await getAllCampaigns(partnerID);
        const list = response.data.cashbackCampaigns;
        const defaultCampaign = list.find((c: CampaignView) => c.isDefault);
        if (defaultCampaign) {
          setCampaigns([defaultCampaign, ...list.filter((c: CampaignView) => !c.isDefault)]);
        } else {
          setCampaigns(list);
        }
      } catch (error) {
        addError(JSON.stringify(error.response));
      } finally {
        setIsLoading(false);
      }
    },
    [setIsLoading, addError],
  );

  useEffect(() => {
    setTranslation(JSON.parse(translations));
  }, [translations]);

  useEffect(() => {
    const userLoginData = JSON.parse(userData);
    setPartnerId(userLoginData.partnerID);

    (async () => {
      await getPayments();
      await getCampaigns(userLoginData.partnerID);
    })();
  }, []);

  const handleExpandedAccordionChange = (campaignId: number) => (event: any, isExpanded: boolean) => {
    setExpanded(isExpanded ? campaignId : false);
  };

  const handleEditCampaign = useCallback(
    async (campaignId: number) => {
      try {
        setIsLoading(true);
        const response = await getCampaign(campaignId);
        setSelectedCampaign(response.data);
        if (response.data.isDefault) {
          setDefaultCampaignDialogTrigger(true);
        } else {
          setCampaignDialogTrigger(true);
        }
      } catch (error) {
        addError(JSON.stringify(error.response));
      } finally {
        setIsLoading(false);
      }
    },
    [setIsLoading, addError],
  );

  const saveChangedCampaign = useCallback(
    async (campaign: EditCampaign) => {
      try {
        setIsLoading(true);
        if (selectedCampaign) {
          await editCampaign(selectedCampaign.id, campaign);
        } else {
          await addCampaign(campaign);
        }
        await getCampaigns(partnerId);
      } catch (error) {
        if (error.response.status === 401) {
          addError(error.response);
        } else {
          const message = concatErrorMessages(translation, error.response.data);
          enqueueSnackbar(message, { variant: 'error' });
        }
      } finally {
        setCampaignDialogTrigger(false);
        setDefaultCampaignDialogTrigger(false);
        setSelectedCampaign(undefined);
        setIsLoading(false);
      }
    },
    [selectedCampaign, setIsLoading, enqueueSnackbar, getCampaigns, partnerId, addError],
  );

  const handleAddNewRule = useCallback(
    async (campaignId: number) => {
      try {
        setIsLoading(true);
        const response = await getRuleSetup(campaignId);
        setSelectedCampaignId(campaignId);
        setRuleSetup(response.data);
        setRuleDialogTrigger(true);
      } catch (error) {
        if (error.response.status === 401) {
          addError(error.response);
        } else {
          const message = concatErrorMessages(translation, error.response.data);
          enqueueSnackbar(message, { variant: 'error' });
        }
      } finally {
        setIsLoading(false);
      }
    },
    [setIsLoading, enqueueSnackbar, addError],
  );

  const handleEditRule = useCallback(
    async (ruleId: number, campaignId: number) => {
      try {
        setIsLoading(true);
        const response = await getRule(ruleId);
        setSelectedRule(response.data);
        const setUpResponse = await getRuleSetup(campaignId);
        setSelectedCampaignId(campaignId);
        setRuleSetup(setUpResponse.data);
        setRuleDialogTrigger(true);
      } catch (error) {
        const message = concatErrorMessages(translation, error.response.data);
        enqueueSnackbar(message, { variant: 'error' });
      } finally {
        setIsLoading(false);
      }
    },
    [setIsLoading, enqueueSnackbar],
  );

  const saveChangedRule = useCallback(
    async (rule: EditCampaignRule, ruleId?: number) => {
      try {
        setIsLoading(true);
        if (ruleId) {
          const response = await editRule(rule, ruleId);
          const currentCampaign = campaigns.find((c) => c.id === rule.cashbackCampaignId);
          if (currentCampaign) {
            currentCampaign.rules = currentCampaign.rules.map((r) => (r.id === ruleId ? response.data : r));
            const updatedData = campaigns.map((c) => (c.id === rule.cashbackCampaignId ? currentCampaign : c));
            setCampaigns(updatedData);
          }
        } else {
          const response = await addRule(rule);
          campaigns.forEach((c) => {
            if (c.id === rule.cashbackCampaignId) {
              c.rules.push(response.data);
              c.totalRules = c.rules.length;
            }
          });
        }
      } catch (error) {
        if (error.response.status === 401) {
          addError(error.response);
        } else {
          const message = concatErrorMessages(translation, error.response.data);
          enqueueSnackbar(message, { variant: 'error' });
        }
      } finally {
        setRuleDialogTrigger(false);
        setSelectedRule(undefined);
        setSelectedCampaignId(undefined);
        setIsLoading(false);
      }
    },
    [setIsLoading, enqueueSnackbar, campaigns, addError],
  );

  const handleDeleteCampaign = useCallback(async () => {
    if (selectedCampaignId) {
      try {
        setIsLoading(true);
        await deleteCampaign(selectedCampaignId);
        await getCampaigns(partnerId);
      } catch (error) {
        addError(JSON.stringify(error.response));
      } finally {
        setDeleteConfirmDialog(false);
        setSelectedCampaign(undefined);
        setIsLoading(false);
      }
    }
  }, [setIsLoading, addError, selectedCampaignId, campaigns, partnerId]);

  const handleDeleteRule = useCallback(
    async (ruleId: number, campaignId: number) => {
      try {
        setIsLoading(true);
        await deleteRule(ruleId);
        campaigns.forEach((c) => {
          if (c.id === campaignId) {
            c.rules = c.rules.filter((r) => r.id !== ruleId);
            c.rules.map((r, index) => {
              if (r.priority !== index + 1) {
                r.priority = index + 1;
              }
              return r;
            });
          }
          c.totalRules = c.rules.length;
        });
        setCampaigns(campaigns);
      } catch (error) {
        addError(JSON.stringify(error.response));
      } finally {
        setSelectedRule(undefined);
        setSelectedCampaignId(undefined);
        setIsLoading(false);
      }
    },
    [setIsLoading, addError, campaigns],
  );

  return (
    <>
      <Header title={translation.client_cashback} />
      <Grid container direction="column" className={classes.root}>
        <PageTitle title={translation.client_cashback} />
        <Grid container direction="column" className={commonClasses.mainContainer}>
          <Grid item xs={12} className={classes.paper}>
            <Grid item xs={12} className={commonClasses.header}>
              {translation.campaigns}
            </Grid>
            <Grid item xs={12} className={commonClasses.contentWithAccordion}>
              <Grid>
                {campaigns.slice(0, rowsNumber).map((campaign) => (
                  <Accordion classes={{ root: commonClasses.accordion }} key={campaign.id} expanded={expanded === campaign.id} onChange={handleExpandedAccordionChange(campaign.id)}>
                    <AccordionSummary classes={{ content: commonClasses.accordionContent }} expandIcon={<ExpandLess />}>
                      <Grid item>
                        <CashbackItemIcon />
                      </Grid>
                      <Grid container direction="column" style={{ marginInlineStart: 10 }}>
                        <Typography className={commonClasses.accordionHeader}>{campaign.name === 'Default' ? translation.default : campaign.name}</Typography>
                        <Typography className={commonClasses.accordionSubHeader}>
                          {campaign.periodType === 'Permanent' ? translation.campaign_period_permanent : `${formatDate(campaign.startDate, locale)} - ${formatDate(campaign.endDate, locale)}`}
                        </Typography>
                      </Grid>
                      <CampaignEnableSwitch translation={translation} campaign={campaign} />
                    </AccordionSummary>
                    <AccordionDetails classes={{ root: classes.accordionDetails }}>
                      <Grid container direction="column" alignItems="flex-start" alignContent="flex-start">
                        <Grid container direction="row" alignContent="flex-start" alignItems="center" justify="space-between" spacing={2} className={classes.contentMargin}>
                          <Grid item xs={4}>
                            <QuickReport title={translation.clients} value={campaign.totalClients} />
                          </Grid>
                          <Grid item xs={4}>
                            <QuickReport title={translation.campaign_rules} value={campaign.totalRules} />
                          </Grid>
                          <Grid item xs={4}>
                            <QuickReport
                              title={translation.campaign_calculation}
                              value={campaign.calculationPeriod === Object.values(CalculationPeriod)[0] ? translation.text_1269 : campaign.calculationPeriod === Object.values(CalculationPeriod)[1] ? translation.weekly : translation.text_1270}
                            />
                          </Grid>
                        </Grid>
                        <Grid container direction="row" alignContent="flex-start" alignItems="center" className={classes.rulesTitlePanel}>
                          <Typography className={classes.rulesTitle}>{translation.campaign_rules}</Typography>
                        </Grid>
                        <Grid container direction="row">
                          <RulesTable key={campaign.id} translation={translation} rules={campaign.rules} onEdit={(ruleId: number) => handleEditRule(ruleId, campaign.id)} onDelete={(ruleId: number) => handleDeleteRule(ruleId, campaign.id)} />
                        </Grid>
                        <Grid container direction="row" alignItems="flex-start" alignContent="flex-start" className={classes.margin}>
                          <Button
                            startIcon={<AddIcon />}
                            className={commonClasses.editBtn}
                            onClick={async () => {
                              await handleAddNewRule(campaign.id);
                            }}
                          >
                            {translation.new_rule}
                          </Button>
                          <Button
                            startIcon={<EditIcon />}
                            className={commonClasses.editBtn}
                            onClick={async (event) => {
                              event.stopPropagation();
                              await handleEditCampaign(campaign.id);
                            }}
                          >
                            {translation.text_471}
                          </Button>
                          {!campaign.isDefault && (
                            <Button
                              startIcon={<DeleteIcon />}
                              className={commonClasses.deleteBtn}
                              onClick={() => {
                                setSelectedCampaignId(campaign.id);
                                setDeleteConfirmDialog(true);
                              }}
                            >
                              {translation.text_4}
                            </Button>
                          )}
                        </Grid>
                      </Grid>
                    </AccordionDetails>
                  </Accordion>
                ))}
              </Grid>
              <Grid>
                {campaigns.length > 0 && campaigns.length > rowsNumber && (
                  <Grid container direction="row" alignContent="center" alignItems="center" className={classes.margin} justify="center">
                    <Grid item className={commonClasses.tableCustomPaging}>
                      {translation.text_236} {`1 - ${campaigns.length > rowsNumber ? `${rowsNumber} of ${campaigns.length}` : campaigns.length}`} {campaigns.length > 1 ? translation.text_237 : translation.text_238}
                    </Grid>
                    {campaigns.length > rowsNumber && (
                      <Grid item>
                        <Button className={commonClasses.showMoreRowsBtn} onClick={handleShowMore}>
                          {translation.text_239}
                        </Button>
                      </Grid>
                    )}
                  </Grid>
                )}
              </Grid>
            </Grid>
            <Grid item xs={12} style={{ marginTop: 10 }}>
              <Grid container direction="row" alignItems="center" style={{ padding: 16, height: 88 }}>
                <Grid item>
                  <Button
                    onClick={() => {
                      setCampaignDialogTrigger(true);
                      setSelectedCampaign(undefined);
                    }}
                    style={{ padding: 0 }}
                  >
                    <RoundBtnIcon />
                  </Button>
                </Grid>
                <Grid item style={{ marginInlineStart: 8 }}>
                  <Grid container direction="column">
                    <Typography className={commonClasses.accordionHeader}>{translation.new_campaign}</Typography>
                    <Grid container direction="row" alignItems="center">
                      <Typography className={commonClasses.accordionSubHeader} />
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
          <Grid item xs={12} className={`${classes.contentMargin} ${classes.paper}`}>
            <Grid item xs={12} className={commonClasses.header}>
              {translation.campaign_payments}
            </Grid>
            <Grid item xs={12}>
              <PaymentsTable translation={translation} payments={payments} locale={locale || 'en'} />
            </Grid>
          </Grid>
        </Grid>

        {partnerId && campaignDialogTrigger && (
          <AddEditCampaignDialog
            partnerId={partnerId}
            translation={translation}
            open={campaignDialogTrigger}
            handleCancel={() => {
              setCampaignDialogTrigger(false);
              setSelectedCampaign(undefined);
            }}
            campaign={selectedCampaign}
            onSave={(campaign) => saveChangedCampaign(campaign)}
          />
        )}

        {partnerId && selectedCampaign && (
          <EditDefaultCampaignDialog
            partnerId={partnerId}
            translation={translation}
            open={defaultCampaignDialogTrigger}
            handleCancel={() => {
              setDefaultCampaignDialogTrigger(false);
              setSelectedCampaign(undefined);
            }}
            campaign={selectedCampaign}
            onSave={(campaign) => saveChangedCampaign(campaign)}
          />
        )}

        {ruleSetup && selectedCampaignId && (
          <AddEditRuleDialog
            open={ruleDialogTrigger}
            translation={translation}
            campaignId={selectedCampaignId}
            ruleSetup={ruleSetup}
            handleCancel={() => {
              setRuleDialogTrigger(false);
              setSelectedRule(undefined);
              setSelectedCampaignId(undefined);
            }}
            rule={selectedRule}
            onSave={saveChangedRule}
          />
        )}

        <DeleteConfirmationDialog open={deleteConfirmDialog} translation={translation} handleCancel={() => setDeleteConfirmDialog(false)} onDelete={handleDeleteCampaign} />
      </Grid>
    </>
  );
};
